import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardPasswordGate from '../analytics-dashboard/components/DashboardPasswordGate';
import { fetchAllSettings, saveAllSettings, isAuthenticated } from '../../services/apiService';
import { buildCustomerEmail, buildAdminEmail } from '../../utils/emailTemplates';


const SECTIONS = [
  { id: 'berater', label: 'Berater & Verfuegbarkeit', icon: '👤' },
  { id: 'kalender', label: 'Kalender-Ansicht', icon: '📅' },
  { id: 'email-templates', label: 'E-Mail-Vorlagen', icon: '✉️' },
  { id: 'notifications', label: 'Benachrichtigungen', icon: '🔔' },
  { id: 'booking', label: 'Buchungseinstellungen', icon: '📅' },
  { id: 'platform', label: 'Plattform & Branding', icon: '🏢' },
  { id: 'integrations', label: 'Integrationen', icon: '🔗' },
];

const WEEKDAY_LABELS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const TEMPLATE_VARS = ['vorname', 'nachname', 'email', 'telefon', 'wohnort', 'date', 'time', 'berater_name', 'consultation_type', 'booking_ref', 'outlook_link', 'webinar_id'];

const getDefaultSettings = () => ({
  berater: [
    {
      id: 'rainer',
      name: 'Rainer Gottula',
      email: import.meta.env?.VITE_BERATER_1_EMAIL || 'gottula@zba.academy',
      role: 'Projektmanagement',
      active: true,
      calendarSync: true,
      workingHours: { start: '07:30', end: '16:30' },
      workingDays: [1, 2, 3, 4, 5],
      bufferMinutes: 0,
      maxBookingsPerDay: 6,
      phone: '+49 2058 7792163',
      note: 'Bevorzugt Vor-Ort-Beratungen',
    },
    {
      id: 'roger',
      name: 'Roger Szielenkewitz',
      email: import.meta.env?.VITE_BERATER_2_EMAIL || 'szielenkewitz@zba.academy',
      role: 'Berater',
      active: true,
      calendarSync: true,
      workingHours: { start: '07:30', end: '16:30' },
      workingDays: [1, 2, 3, 4, 5],
      bufferMinutes: 0,
      maxBookingsPerDay: 8,
      phone: '',
      note: '',
    },
  ],
  notifications: {
    adminEmails: [
      import.meta.env?.VITE_BERATER_1_EMAIL || 'gottula@zba.academy',
      import.meta.env?.VITE_BERATER_2_EMAIL || 'szielenkewitz@zba.academy',
    ],
    ownAdminEmail: 'w.saris@zba.academy',
    onlyNotifyAdmin: false,
    sendCustomerConfirmation: true,
    sendAdminAlert: true,
    sendReminderEmail: true,
    reminderHoursBefore: 24,
    sendCancellationNotice: true,
    bccAllEmails: '',
    replyToEmail: import.meta.env?.VITE_SES_FROM_EMAIL || 'noreply@zba.academy',
    fromName: 'ZBA Academy',
  },
  booking: {
    advanceBookingDays: 60,
    minAdvanceHours: 24,
    allowWeekends: false,
    requireDSGVO: true,
    showWebinarField: true,
    autoConfirm: true,
    bookingRefPrefix: 'ZBA',
    maxSlotsPerDay: 8,
    cancellationWindowHours: 24,
    consultationTypes: [
      { id: 'onsite', label: 'Vor-Ort-Beratung', duration: 60, active: true },
      { id: 'phone', label: 'Telefonberatung', duration: 30, active: true },
    ],
  },
  platform: {
    companyName: 'ZBA Academy',
    companyAddress: 'Kruppstraße 41-45, 42489 Wülfrath',
    companyPhone: '+49 2058 123456',
    companyEmail: 'info@zba.academy',
    companyWebsite: 'https://zba.academy',
    primaryColor: '#3E4954',
    accentColor: '#1e2b38',
    dashboardPassword: 'zba2026',
    timezone: 'Europe/Berlin',
    language: 'de',
    dsgvoText: 'Ich stimme der Verarbeitung meiner personenbezogenen Daten gemaess der Datenschutzerklaerung der ZBA Academy zu.',
  },
  integrations: {
    msGraphEnabled: true, // Status via /api/health?graph=true
    msTenantId: '(server-side)',
    msClientId: '(server-side)',
    sesEnabled: !!(import.meta.env?.VITE_SES_SMTP_USER && import.meta.env?.VITE_SES_SMTP_USER !== 'your-ses-smtp-username-here'),
    sesHost: import.meta.env?.VITE_SES_SMTP_HOST || 'email-smtp.eu-central-1.amazonaws.com',
    sesPort: import.meta.env?.VITE_SES_SMTP_PORT || '587',
    sesFromEmail: import.meta.env?.VITE_SES_FROM_EMAIL || '',
    gaEnabled: !!(import.meta.env?.VITE_GA_MEASUREMENT_ID && import.meta.env?.VITE_GA_MEASUREMENT_ID !== 'your-google-analytics-id-here'),
    gaMeasurementId: import.meta.env?.VITE_GA_MEASUREMENT_ID || '',
  },
  emailTemplates: {
    customerSubject: 'Terminbestaetigung - {{booking_ref}}',
    customerBody: 'Sehr geehrte/r {{vorname}} {{nachname}},\n\nvielen Dank fuer Ihre Buchung bei der ZBA Academy. Ihr Termin ist bestaetigt.\n\nDatum: {{date}}\nUhrzeit: {{time}} Uhr\nBerater: {{berater_name}}\nArt: {{consultation_type}}\nBuchungs-Nr.: {{booking_ref}}\n\nKalender-Link: {{outlook_link}}\n\nMit freundlichen Gruessen,\nIhr ZBA Academy Team',
    adminSubject: 'Neue Buchung - {{vorname}} {{nachname}} - {{booking_ref}}',
    adminBody: 'Neue Buchungsanfrage:\n\nKunde: {{vorname}} {{nachname}}\nE-Mail: {{email}}\nTelefon: {{telefon}}\nWohnort: {{wohnort}}\n\nTermin: {{date}} um {{time}} Uhr\nArt: {{consultation_type}}\nBerater: {{berater_name}}\nBuchungs-Nr.: {{booking_ref}}\n\nKalender-Link: {{outlook_link}}',
    reminderSubject: 'Erinnerung: Ihr Termin morgen - {{booking_ref}}',
    reminderBody: 'Sehr geehrte/r {{vorname}} {{nachname}},\n\nwir moechten Sie an Ihren morgigen Termin erinnern:\n\n{{date}} um {{time}} Uhr\nBerater: {{berater_name}}\n\nBis morgen!\nIhr ZBA Academy Team',
  },
});

const SectionCard = ({ title, subtitle = null, children }) => (
  React.createElement('div', {
    style: { background: 'white', borderRadius: '16px', border: '1px solid #e8eaed', marginBottom: '20px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(30,43,56,0.06)' }
  },
    React.createElement('div', { style: { padding: '20px 24px 16px', borderBottom: '1px solid #f0f2f5' } },
      React.createElement('div', { style: { fontSize: '15px', fontWeight: 700, color: '#1e2b38', fontFamily: 'var(--font-heading)' } }, title),
      subtitle && React.createElement('div', { style: { fontSize: '12px', color: '#8a95a3', marginTop: '3px', fontFamily: 'var(--font-ui)' } }, subtitle)
    ),
    React.createElement('div', { style: { padding: '20px 24px' } }, children)
  )
);

const FieldRow = ({ label, hint = null, children }) => (
  React.createElement('div', { style: { display: 'flex', alignItems: 'flex-start', gap: '24px', marginBottom: '18px' } },
    React.createElement('div', { style: { width: '200px', flexShrink: 0, paddingTop: '8px' } },
      React.createElement('div', { style: { fontSize: '13px', fontWeight: 600, color: '#3E4954', fontFamily: 'var(--font-ui)' } }, label),
      hint && React.createElement('div', { style: { fontSize: '11px', color: '#a0aab4', marginTop: '2px', lineHeight: 1.4 } }, hint)
    ),
    React.createElement('div', { style: { flex: 1 } }, children)
  )
);

const inputStyle = (disabled) => ({
  width: '100%', padding: '8px 12px', borderRadius: '8px',
  border: '1.5px solid #dde1e7', fontSize: '13px',
  fontFamily: 'var(--font-ui)', color: '#1e2b38',
  background: disabled ? '#f8f9fa' : 'white',
  outline: 'none', boxSizing: 'border-box',
});

const TextInput = ({ value, onChange, placeholder = '', type = 'text', disabled = false }) => (
  React.createElement('input', {
    type: type || 'text',
    value: value || '',
    onChange: e => onChange && onChange(e?.target?.value),
    placeholder: placeholder || '',
    disabled: disabled || false,
    style: inputStyle(disabled),
    onFocus: e => { if (!disabled) e.target.style.borderColor = '#3E4954'; },
    onBlur: e => { e.target.style.borderColor = '#dde1e7'; },
  })
);

const Toggle = ({ checked, onChange, label }) => (
  React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' } },
    React.createElement('div', {
      onClick: () => onChange && onChange(!checked),
      style: { width: '40px', height: '22px', borderRadius: '11px', background: checked ? '#3E4954' : '#d1d5db', position: 'relative', cursor: 'pointer', transition: 'background 200ms', flexShrink: 0 }
    },
      React.createElement('div', {
        style: { position: 'absolute', top: '3px', left: checked ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 200ms' }
      })
    ),
    label && React.createElement('span', { style: { fontSize: '13px', color: '#3E4954', fontFamily: 'var(--font-ui)', fontWeight: 500 } }, label)
  )
);

const SelectInput = ({ value, onChange, options }) => (
  React.createElement('select', {
    value: value,
    onChange: e => onChange && onChange(e?.target?.value),
    style: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #dde1e7', fontSize: '13px', fontFamily: 'var(--font-ui)', color: '#1e2b38', background: 'white', outline: 'none', cursor: 'pointer', minWidth: '160px' }
  },
    options?.map(o => React.createElement('option', { key: o?.value, value: o?.value }, o?.label))
  )
);

const NumberInput = ({ value, onChange, min, max, suffix }) => (
  React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
    React.createElement('input', {
      type: 'number', value: value, min: min, max: max,
      onChange: e => onChange && onChange(parseInt(e?.target?.value, 10) || 0),
      style: { width: '80px', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #dde1e7', fontSize: '13px', fontFamily: 'var(--font-ui)', color: '#1e2b38', background: 'white', outline: 'none', textAlign: 'center' },
      onFocus: e => { e.target.style.borderColor = '#3E4954'; },
      onBlur: e => { e.target.style.borderColor = '#dde1e7'; },
    }),
    suffix && React.createElement('span', { style: { fontSize: '12px', color: '#8a95a3', fontFamily: 'var(--font-ui)' } }, suffix)
  )
);

const TextArea = ({ value, onChange, rows, placeholder }) => (
  React.createElement('textarea', {
    value: value || '',
    onChange: e => onChange && onChange(e?.target?.value),
    rows: rows || 6,
    placeholder: placeholder || '',
    style: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #dde1e7', fontSize: '13px', fontFamily: 'var(--font-ui)', color: '#1e2b38', background: 'white', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6 },
    onFocus: e => { e.target.style.borderColor = '#3E4954'; },
    onBlur: e => { e.target.style.borderColor = '#dde1e7'; },
  })
);

const StatusBadge = ({ active, trueLabel = 'Aktiv', falseLabel = 'Inaktiv' }) => (
  React.createElement('span', {
    style: { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, fontFamily: 'var(--font-ui)', background: active ? '#dcfce7' : '#fee2e2', color: active ? '#16a34a' : '#dc2626' }
  },
    React.createElement('span', { style: { width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' } }),
    active ? trueLabel : falseLabel
  )
);



// ── Calendar Debug View ────────────────────────────────────────────────────
function CalendarViewSection() {
  const [email, setEmail] = React.useState('');
  const [beraters, setBeraters] = React.useState([]);
  const [weekStart, setWeekStart] = React.useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const auth = { Authorization: `Bearer ${sessionStorage.getItem('zba_admin_token')}` };

  React.useEffect(() => {
    fetch('/api/admin/berater', { headers: auth }).then(r=>r.json()).then(d => {
      const all = (d.all||[]).map(b=>({name:b.name,email:b.email,initials:b.initials}));
      setBeraters(all);
      if (all.length && !email) setEmail(all[0].email);
    }).catch(()=>{});
  }, []);

  const weekDates = React.useMemo(() => {
    const d = new Date(weekStart + 'T12:00:00');
    return Array.from({length:5}, (_,i) => {
      const x = new Date(d); x.setDate(d.getDate()+i);
      return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`;
    });
  }, [weekStart]);

  const load = () => {
    if (!email) return;
    setLoading(true);
    const endDate = weekDates[weekDates.length-1];
    fetch(`/api/admin/calendar-view?email=${encodeURIComponent(email)}&startDate=${weekDates[0]}&endDate=${endDate}`, { headers: auth })
      .then(r=>r.json()).then(d => { setData(d); setLoading(false); }).catch(()=>setLoading(false));
  };
  React.useEffect(load, [email, weekStart]);

  const prevWeek = () => { const d = new Date(weekStart+'T12:00:00'); d.setDate(d.getDate()-7); setWeekStart(d.toISOString().slice(0,10)); };
  const nextWeek = () => { const d = new Date(weekStart+'T12:00:00'); d.setDate(d.getDate()+7); setWeekStart(d.toISOString().slice(0,10)); };

  const HOURS = [7,8,9,10,11,12,13,14,15,16,17];
  const DAYS_DE = ['Mo','Di','Mi','Do','Fr'];
  const evColor = (ev) => ev.source==='zba' ? {bg:'#E8F5E9',border:'#4CAF50',text:'#1B5E20'} : ev.showAs==='busy' ? {bg:'#DBEAFE',border:'#3B82F6',text:'#1E40AF'} : ev.showAs==='tentative' ? {bg:'#FEF3C7',border:'#F59E0B',text:'#92400E'} : {bg:'#F3E8FF',border:'#8B5CF6',text:'#5B21B6'};

  const filteredBeraters = beraters.filter(b => !search || b.name.toLowerCase().includes(search.toLowerCase()) || b.email.toLowerCase().includes(search.toLowerCase()));

  const allEvents = data ? [...(data.events||[]),...(data.zbaEvents||[])] : [];

  return (
    <div>
      <div style={{marginBottom:20}}>
        <h2 style={{fontSize:20,fontWeight:700,color:'#1e2b38',margin:0}}>Kalender-Ansicht</h2>
        <p style={{fontSize:13,color:'#8a95a3',margin:'4px 0 0'}}>Kalender eines Mitarbeiters einsehen (persoenlich + ZBA-Kalender).</p>
      </div>

      {/* User picker */}
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <div style={{position:'relative',flex:'0 0 280px'}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Mitarbeiter suchen..."
            style={{width:'100%',padding:'10px 14px 10px 36px',border:'1px solid #dde1e7',borderRadius:8,fontSize:14,fontFamily:'var(--font-ui)',outline:'none'}} />
          <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#aab3bd'}}>🔍</span>
        </div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
          {filteredBeraters.map(b => (
            <button key={b.email} onClick={()=>{setEmail(b.email);setSearch('');}}
              style={{padding:'8px 14px',borderRadius:8,border:`2px solid ${email===b.email?'#1e2b38':'#dde1e7'}`,background:email===b.email?'#1e2b38':'#fff',color:email===b.email?'white':'#5a6472',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'var(--font-ui)',display:'flex',alignItems:'center',gap:6}}>
              <span style={{width:22,height:22,borderRadius:'50%',background:email===b.email?'#fff':'#1e2b38',color:email===b.email?'#1e2b38':'#fff',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700}}>{b.initials}</span>
              {b.name}
            </button>
          ))}
        </div>
        {/* Manual email input */}
        <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="oder E-Mail eingeben..."
          style={{flex:1,minWidth:200,padding:'10px 14px',border:'1px solid #dde1e7',borderRadius:8,fontSize:13,fontFamily:'var(--font-ui)',outline:'none',color:'#5a6472'}} />
      </div>

      {/* Week nav */}
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
        <button onClick={prevWeek} style={{width:36,height:36,borderRadius:8,border:'1px solid #dde1e7',background:'#fff',cursor:'pointer',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center'}}>‹</button>
        <span style={{fontSize:15,fontWeight:700,color:'#1e2b38',fontFamily:'var(--font-ui)',minWidth:200,textAlign:'center'}}>
          {weekDates[0] && new Date(weekDates[0]+'T12:00:00').toLocaleDateString('de-DE',{day:'numeric',month:'long'})} — {weekDates[4] && new Date(weekDates[4]+'T12:00:00').toLocaleDateString('de-DE',{day:'numeric',month:'long',year:'numeric'})}
        </span>
        <button onClick={nextWeek} style={{width:36,height:36,borderRadius:8,border:'1px solid #dde1e7',background:'#fff',cursor:'pointer',fontSize:16,display:'flex',alignItems:'center',justifyContent:'center'}}>›</button>
        {loading && <span style={{fontSize:12,color:'#8a95a3'}}>Laden...</span>}
      </div>

      {/* Calendar grid */}
      <div style={{background:'#fff',borderRadius:14,border:'1px solid #e2e6ea',overflow:'hidden'}}>
        {/* Header */}
        <div style={{display:'grid',gridTemplateColumns:'60px repeat(5,1fr)',borderBottom:'1px solid #e2e6ea'}}>
          <div style={{padding:8,background:'#f8f9fb'}}></div>
          {weekDates.map((d,i) => {
            const dt = new Date(d+'T12:00:00');
            const isToday = d === new Date().toISOString().slice(0,10);
            const isBlocked = data?.summary?.allDayDates?.includes(d);
            return (
              <div key={d} style={{padding:'10px 8px',textAlign:'center',background:isBlocked?'#FEF2F2':isToday?'#EFF6FF':'#f8f9fb',borderLeft:'1px solid #e2e6ea'}}>
                <div style={{fontSize:11,fontWeight:700,color:'#8a95a3',textTransform:'uppercase'}}>{DAYS_DE[i]}</div>
                <div style={{fontSize:18,fontWeight:700,color:isToday?'#2563EB':isBlocked?'#DC2626':'#1e2b38'}}>{dt.getDate()}</div>
                {isBlocked && <div style={{fontSize:9,color:'#DC2626',fontWeight:600,marginTop:2}}>BLOCKIERT</div>}
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <div style={{position:'relative'}}>
          {HOURS.map(h => (
            <div key={h} style={{display:'grid',gridTemplateColumns:'60px repeat(5,1fr)',height:52,borderBottom:'1px solid #f0f2f5'}}>
              <div style={{padding:'4px 8px',fontSize:11,color:'#aab3bd',textAlign:'right',fontFamily:'var(--font-ui)'}}>{String(h).padStart(2,'0')}:00</div>
              {weekDates.map(d => (
                <div key={d} style={{borderLeft:'1px solid #f0f2f5',position:'relative'}}></div>
              ))}
            </div>
          ))}

          {/* Render events */}
          {allEvents.map((ev,idx) => {
            const startDT = ev.start.slice(0,10);
            const dayIdx = weekDates.indexOf(startDT);
            if (dayIdx < 0) return null;
            if (ev.isAllDay) return null; // all-day shown in header

            const startH = parseInt(ev.start.slice(11,13));
            const startM = parseInt(ev.start.slice(14,16));
            const endH = parseInt(ev.end.slice(11,13));
            const endM = parseInt(ev.end.slice(14,16));
            const topPx = ((startH - 7) * 52) + (startM / 60 * 52);
            const heightPx = Math.max(20, ((endH - startH) * 52) + ((endM - startM) / 60 * 52));
            const col = evColor(ev);

            return (
              <div key={idx} title={`${ev.subject}\n${ev.start.slice(11,16)} - ${ev.end.slice(11,16)}\nshowAs: ${ev.showAs}\nQuelle: ${ev.source}`}
                style={{position:'absolute',top:topPx,left:`calc(60px + ${dayIdx} * (100% - 60px) / 5 + 3px)`,width:`calc((100% - 60px) / 5 - 6px)`,height:heightPx,
                  background:col.bg,borderLeft:`3px solid ${col.border}`,borderRadius:4,padding:'3px 6px',overflow:'hidden',cursor:'default',fontSize:11,lineHeight:1.3,color:col.text,zIndex:2}}>
                <div style={{fontWeight:700,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{ev.subject}</div>
                <div style={{fontSize:10,opacity:0.7}}>{ev.start.slice(11,16)}-{ev.end.slice(11,16)} · {ev.source==='zba'?'ZBA':'Persoenlich'}</div>
              </div>
            );
          })}

          {/* All-day events as banner at top */}
          {allEvents.filter(ev=>ev.isAllDay).map((ev,idx) => {
            const startDT = ev.start.slice(0,10);
            const dayIdx = weekDates.indexOf(startDT);
            if (dayIdx < 0) return null;
            const col = evColor(ev);
            return (
              <div key={'ad'+idx} title={`${ev.subject} (Ganztags)\nshowAs: ${ev.showAs}\nQuelle: ${ev.source}`}
                style={{position:'absolute',top:-2,left:`calc(60px + ${dayIdx} * (100% - 60px) / 5 + 2px)`,width:`calc((100% - 60px) / 5 - 4px)`,
                  background:col.bg,borderLeft:`3px solid ${col.border}`,borderRadius:4,padding:'2px 6px',overflow:'hidden',fontSize:10,color:col.text,fontWeight:600,zIndex:3}}>
                {ev.subject}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{display:'flex',gap:16,marginTop:12,fontSize:11,color:'#8a95a3'}}>
        <span><span style={{display:'inline-block',width:10,height:10,borderRadius:2,background:'#DBEAFE',border:'1px solid #3B82F6',marginRight:4,verticalAlign:'middle'}}></span>Persoenlich (busy)</span>
        <span><span style={{display:'inline-block',width:10,height:10,borderRadius:2,background:'#FEF3C7',border:'1px solid #F59E0B',marginRight:4,verticalAlign:'middle'}}></span>Persoenlich (tentative)</span>
        <span><span style={{display:'inline-block',width:10,height:10,borderRadius:2,background:'#E8F5E9',border:'1px solid #4CAF50',marginRight:4,verticalAlign:'middle'}}></span>ZBA-Kalender</span>
        <span><span style={{display:'inline-block',width:10,height:10,borderRadius:2,background:'#FEF2F2',border:'1px solid #DC2626',marginRight:4,verticalAlign:'middle'}}></span>Ganztags blockiert</span>
      </div>
    </div>
  );
}

// ── Dynamic Berater Management ─────────────────────────────────────────────
function BeraterSection() {
  const [beraters, setBeraters] = React.useState([]);
  const [durations, setDurations] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [editId, setEditId] = React.useState(null);
  const [showAdd, setShowAdd] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [form, setForm] = React.useState({ name:'', email:'', initials:'', sources:['regular'], working_hours_start:'07:30', working_hours_end:'16:30', working_days:[1,2,3,4,5], active:true, paused:false });
  const authHeader = { Authorization: `Bearer ${sessionStorage.getItem('zba_admin_token')}` };
  const load = () => { setLoading(true); fetch('/api/admin/berater', { headers: authHeader }).then(r => r.json()).then(d => { setBeraters(d.all || []); setDurations(d.durations || {}); setLoading(false); }).catch(() => setLoading(false)); };
  React.useEffect(load, []);
  const save = async () => { await fetch('/api/admin/berater', { method:'POST', headers:{...authHeader,'Content-Type':'application/json'}, body:JSON.stringify(form) }); setShowAdd(false); setEditId(null); load(); };
  const togglePause = async (id) => { await fetch(`/api/admin/berater/${id}/pause`, { method:'PATCH', headers:authHeader }); load(); };
  const remove = async (id) => { if (!confirm('Berater wirklich entfernen?')) return; await fetch(`/api/admin/berater/${id}`, { method:'DELETE', headers:authHeader }); load(); };
  const startEdit = (b) => { setForm({ name:b.name, email:b.email, initials:b.initials, sources:b.sources||[], working_hours_start:b.working_hours_start||'07:30', working_hours_end:b.working_hours_end||'16:30', working_days:b.working_days||[1,2,3,4,5], active:!!b.active, paused:!!b.paused }); setEditId(b.id); setShowAdd(true); };
  const resetForm = () => setForm({ name:'', email:'', initials:'', sources:['regular'], working_hours_start:'07:30', working_hours_end:'16:30', working_days:[1,2,3,4,5], active:true, paused:false });
  const filtered = beraters.filter(b => !search || b.name.toLowerCase().includes(search.toLowerCase()) || b.email.toLowerCase().includes(search.toLowerCase()));
  const SOURCES = [{id:'regular',label:'Interessenten',color:'#3E4954'},{id:'institutionen',label:'Institutionen',color:'#5B21B6'}];
  const DAYS = [{d:1,l:'Mo'},{d:2,l:'Di'},{d:3,l:'Mi'},{d:4,l:'Do'},{d:5,l:'Fr'}];
  const S = { card:{background:'#fff',borderRadius:14,border:'1px solid #e2e6ea',overflow:'hidden',marginBottom:12}, label:{fontSize:11,fontWeight:700,color:'#6b7a8d',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8,display:'block'}, input:{width:'100%',padding:'10px 14px',border:'1px solid #dde1e7',borderRadius:8,fontSize:14,fontFamily:'var(--font-ui)',outline:'none'}, badge:(bg,col)=>({display:'inline-flex',padding:'3px 10px',borderRadius:100,fontSize:10,fontWeight:700,background:bg,color:col,textTransform:'uppercase',letterSpacing:'0.04em'}) };
  return (
    <div>
      <div style={{marginBottom:20}}>
        <h2 style={{fontSize:20,fontWeight:700,color:'#1e2b38',margin:0}}>Berater verwalten</h2>
        <p style={{fontSize:13,color:'#8a95a3',margin:'4px 0 0'}}>Berater hinzufuegen, pausieren und Terminarten zuordnen.</p>
      </div>
      {durations.regular && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:20}}>
          <div style={{padding:'12px 16px',background:'#F0F6FB',borderRadius:10,border:'1px solid #D0E5F5'}}>
            <span style={S.label}>Interessenten</span>
            <span style={{fontSize:13,color:'#3E4954'}}>Vor-Ort: {durations.regular?.onsite} Min · Tel: {durations.regular?.phone} Min</span>
          </div>
          <div style={{padding:'12px 16px',background:'#F5F0FF',borderRadius:10,border:'1px solid #DDD6FE'}}>
            <span style={S.label}>Institutionen</span>
            <span style={{fontSize:13,color:'#5B21B6'}}>Vor-Ort: {durations.institutionen?.onsite} Min · Tel: {durations.institutionen?.phone} Min</span>
          </div>
        </div>
      )}
      <div style={{display:'flex',gap:10,marginBottom:16}}>
        <div style={{flex:1,position:'relative'}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Berater suchen..." style={{...S.input,paddingLeft:36}} />
          <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#aab3bd'}}>🔍</span>
        </div>
        <button onClick={()=>{setShowAdd(true);setEditId(null);resetForm();}} style={{padding:'0 20px',height:42,background:'#1e2b38',color:'white',border:'none',borderRadius:10,fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'var(--font-ui)',whiteSpace:'nowrap'}}>+ Hinzufuegen</button>
      </div>
      {showAdd && (
        <div style={{...S.card,padding:24,marginBottom:16,border:'2px solid #3E4954'}}>
          <h3 style={{fontSize:16,fontWeight:700,color:'#1e2b38',marginBottom:16}}>{editId ? 'Berater bearbeiten' : 'Neuer Berater'}</h3>
          <div style={{display:'grid',gridTemplateColumns:'2fr 2fr 1fr',gap:12,marginBottom:14}}>
            <div><span style={S.label}>Name</span><input style={S.input} value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="Max Mustermann" /></div>
            <div><span style={S.label}>E-Mail (MS365)</span><input style={S.input} value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="m.mustermann@zba.academy" /></div>
            <div><span style={S.label}>Kuerzel</span><input style={S.input} value={form.initials} onChange={e=>setForm(p=>({...p,initials:e.target.value.toUpperCase()}))} placeholder="MM" maxLength={4} /></div>
          </div>
          <div style={{marginBottom:14}}>
            <span style={S.label}>Buchbar fuer</span>
            <div style={{display:'flex',gap:8}}>
              {SOURCES.map(s=>{const on=form.sources?.includes(s.id);return(
                <button key={s.id} type="button" onClick={()=>setForm(p=>({...p,sources:on?p.sources.filter(x=>x!==s.id):[...(p.sources||[]),s.id]}))} style={{padding:'8px 16px',borderRadius:8,border:`2px solid ${on?s.color:'#dde1e7'}`,background:on?s.color+'15':'#fff',cursor:'pointer',fontFamily:'var(--font-ui)',fontSize:13,fontWeight:600,color:on?s.color:'#8a95a3'}}>{on?'✓ ':''}{s.label}</button>
              );})}
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 2fr',gap:12,marginBottom:14}}>
            <div><span style={S.label}>Start</span><input type="time" style={S.input} value={form.working_hours_start} onChange={e=>setForm(p=>({...p,working_hours_start:e.target.value}))} /></div>
            <div><span style={S.label}>Ende</span><input type="time" style={S.input} value={form.working_hours_end} onChange={e=>setForm(p=>({...p,working_hours_end:e.target.value}))} /></div>
            <div><span style={S.label}>Arbeitstage</span>
              <div style={{display:'flex',gap:6}}>{DAYS.map(({d,l})=>{const on=form.working_days?.includes(d);return <button key={d} type="button" onClick={()=>setForm(p=>({...p,working_days:on?p.working_days.filter(x=>x!==d):[...(p.working_days||[]),d].sort()}))} style={{width:36,height:36,borderRadius:8,border:`2px solid ${on?'#3E4954':'#dde1e7'}`,background:on?'#3E4954':'#fff',color:on?'white':'#8a95a3',fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'var(--font-ui)'}}>{l}</button>;})}</div>
            </div>
          </div>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button onClick={()=>{setShowAdd(false);setEditId(null);}} style={{padding:'10px 20px',border:'1px solid #dde1e7',borderRadius:8,background:'#fff',cursor:'pointer',fontSize:13,fontFamily:'var(--font-ui)'}}>Abbrechen</button>
            <button onClick={save} disabled={!form.name||!form.email||!form.initials} style={{padding:'10px 24px',border:'none',borderRadius:8,background:!form.name||!form.email||!form.initials?'#dde1e7':'#1e2b38',color:'white',cursor:'pointer',fontSize:13,fontWeight:700,fontFamily:'var(--font-ui)'}}>{editId?'Speichern':'Hinzufuegen'}</button>
          </div>
        </div>
      )}
      {loading ? <div style={{textAlign:'center',padding:40,color:'#8a95a3'}}>Laden...</div> : (
        <div>{filtered.length===0 && <div style={{textAlign:'center',padding:40,color:'#8a95a3'}}>Keine Berater gefunden</div>}
          {filtered.map(b=>(
            <div key={b.id} style={{...S.card,opacity:b.paused?0.55:1}}>
              <div style={{padding:'16px 20px',display:'flex',alignItems:'center',gap:14}}>
                <div style={{width:40,height:40,borderRadius:'50%',background:b.paused?'#dde1e7':'#1e2b38',color:'white',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,flexShrink:0}}>{b.initials}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3}}>
                    <span style={{fontSize:15,fontWeight:700,color:'#1e2b38'}}>{b.name}</span>
                    {b.paused?<span style={S.badge('#FEF3C7','#92400E')}>Pausiert</span>:b.active?<span style={S.badge('#DCFCE7','#166534')}>Aktiv</span>:<span style={S.badge('#F3F4F6','#6B7280')}>Inaktiv</span>}
                  </div>
                  <div style={{fontSize:12,color:'#8a95a3'}}>{b.email}</div>
                  <div style={{display:'flex',gap:6,marginTop:8}}>{(b.sources||[]).map(s=>{const src=SOURCES.find(x=>x.id===s);return src?<span key={s} style={S.badge(s==='institutionen'?'#EDE9FE':'#DBEAFE',s==='institutionen'?'#5B21B6':'#1D4ED8')}>{src.label}</span>:null;})}</div>
                  <div style={{fontSize:11,color:'#aab3bd',marginTop:6}}>{(b.working_days||[]).map(d=>['','Mo','Di','Mi','Do','Fr','Sa','So'][d]).join(', ')} · {b.working_hours_start||'07:30'} - {b.working_hours_end||'16:30'}</div>
                </div>
                <div style={{display:'flex',gap:6,flexShrink:0}}>
                  <button onClick={()=>startEdit(b)} title="Bearbeiten" style={{width:34,height:34,borderRadius:8,border:'1px solid #dde1e7',background:'#fff',cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>✏️</button>
                  <button onClick={()=>togglePause(b.id)} title={b.paused?'Aktivieren':'Pausieren'} style={{width:34,height:34,borderRadius:8,border:'1px solid #dde1e7',background:b.paused?'#FEF3C7':'#fff',cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>{b.paused?'▶️':'⏸'}</button>
                  <button onClick={()=>remove(b.id)} title="Entfernen" style={{width:34,height:34,borderRadius:8,border:'1px solid #FFE4E6',background:'#fff',cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const AdminSettings = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [activeSection, setActiveSection] = useState('berater');
  const [settings, setSettings] = useState(getDefaultSettings);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [expandedBerater, setExpandedBerater] = useState('rainer');
  const [activeEmailTab, setActiveEmailTab] = useState('customer');
  const [activeViewport, setActiveViewport] = useState('desktop');
  const [settingsLoading, setSettingsLoading] = useState(false);

  const updateSettings = useCallback((path, value) => {
    setSettings(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path?.split('.');
      let obj = next;
      for (let i = 0; i < keys?.length - 1; i++) { obj = obj?.[keys?.[i]]; }
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  }, []);

  const updateBerater = useCallback((beraterId, field, value) => {
    setSettings(prev => ({
      ...prev,
      berater: prev?.berater?.map(b => b?.id === beraterId ? { ...b, [field]: value } : b),
    }));
  }, []);

  const updateBeraterNested = useCallback((beraterId, parentField, field, value) => {
    setSettings(prev => ({
      ...prev,
      berater: prev?.berater?.map(b => b?.id === beraterId ? { ...b, [parentField]: { ...b?.[parentField], [field]: value } } : b),
    }));
  }, []);

  // Load settings from Supabase on mount (after authentication)
  useEffect(() => {
    if (!authenticated) return;
    setSettingsLoading(true);
    fetchAllSettings()?.then(map => {
      setSettings(prev => {
        const next = { ...prev };
        if (map?.email_templates) next.emailTemplates = { ...prev?.emailTemplates, ...map?.email_templates };
        if (map?.berater_hours) next.berater = map?.berater_hours;
        if (map?.booking_rules) next.booking = { ...prev?.booking, ...map?.booking_rules };
        if (map?.notification_preferences) next.notifications = { ...prev?.notifications, ...map?.notification_preferences };
        if (map?.branding) next.platform = { ...prev?.platform, ...map?.branding };
        return next;
      });
      setSettingsLoading(false);
    });
  }, [authenticated]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    const sections = {
      email_templates: settings?.emailTemplates,
      berater_hours: settings?.berater,
      booking_rules: settings?.booking,
      notification_preferences: settings?.notifications,
      branding: settings?.platform,
    };
    const result = await saveAllSettings(sections);
    setSaving(false);
    if (result?.success) {
      setSavedAt(new Date());
    } else {
      setSaveError(result?.error || 'Fehler beim Speichern');
    }
  };

  if (!authenticated) {
    return <DashboardPasswordGate onSuccess={() => setAuthenticated(true)} />;
  }

  const btnBase = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '10px', border: '1.5px solid #dde1e7', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#3E4954', fontFamily: 'var(--font-ui)', transition: 'all 150ms ease' };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6f8', fontFamily: 'var(--font-ui)' }}>
      <header style={{ background: 'white', borderBottom: '1px solid #e8eaed', padding: '0 24px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 1px 4px rgba(30,43,56,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: '1.5px solid #dde1e7', background: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#3E4954', fontFamily: 'var(--font-ui)' }}>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'><path d='M19 12H5M12 19l-7-7 7-7'/></svg>
            Dashboard
          </button>
          <div style={{ width: '1px', height: '20px', background: '#e8eaed' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#1e2b38', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><circle cx='12' cy='12' r='3'/><path d='M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14'/><path d='M12 2v2M12 20v2M2 12h2M20 12h2'/></svg>
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e2b38', fontFamily: 'var(--font-heading)', lineHeight: 1.1 }}>Admin-Einstellungen</div>
              <div style={{ fontSize: '11px', color: '#8a95a3' }}>ZBA Academy Plattform-Konfiguration</div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {saveError && <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 500 }}>⚠️ {saveError}</span>}
          {!saveError && savedAt && <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: 500 }}>✓ Gespeichert {savedAt?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</span>}
          {settingsLoading && <span style={{ fontSize: '12px', color: '#8a95a3', fontWeight: 500 }}>Lade Einstellungen...</span>}
          <button onClick={handleSave} disabled={saving || settingsLoading} style={{ padding: '8px 20px', borderRadius: '10px', background: saving ? '#8a95a3' : '#1e2b38', color: 'white', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-ui)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'><path d='M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z'/><polyline points='17 21 17 13 7 13 7 21'/><polyline points='7 3 7 8 15 8'/></svg>
            {saving ? 'Speichern...' : 'Einstellungen speichern'}
          </button>
        </div>
      </header>
      <div style={{ display: 'flex', maxWidth: '1280px', margin: '0 auto', padding: '28px 24px', gap: '24px' }}>
        <nav style={{ width: '220px', flexShrink: 0 }}>
          <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e8eaed', overflow: 'hidden', boxShadow: '0 1px 4px rgba(30,43,56,0.06)', position: 'sticky', top: '80px' }}>
            {SECTIONS?.map((s, i) => (
              <button key={s?.id} onClick={() => setActiveSection(s?.id)} style={{ width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px', background: activeSection === s?.id ? '#f0f4f8' : 'transparent', border: 'none', borderLeft: activeSection === s?.id ? '3px solid #1e2b38' : '3px solid transparent', cursor: 'pointer', textAlign: 'left', borderBottom: i < SECTIONS?.length - 1 ? '1px solid #f5f6f8' : 'none' }}>
                <span style={{ fontSize: '16px' }}>{s?.icon}</span>
                <span style={{ fontSize: '13px', fontWeight: activeSection === s?.id ? 700 : 500, color: activeSection === s?.id ? '#1e2b38' : '#5a6472', fontFamily: 'var(--font-ui)' }}>{s?.label}</span>
              </button>
            ))}
          </div>
        </nav>

        <div style={{ flex: 1, minWidth: 0 }}>

          {activeSection === 'kalender' && (
            <CalendarViewSection />
          )}

          {activeSection === 'berater' && (
            <BeraterSection />
          )}

          {activeSection === 'email-templates' && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e2b38', fontFamily: 'var(--font-heading)', margin: 0 }}>E-Mail-Vorlagen</h2>
                <p style={{ fontSize: '13px', color: '#8a95a3', margin: '4px 0 0' }}>Live-Vorschau der HTML-E-Mails die automatisch an Kunden und Berater gesendet werden.</p>
              </div>

              {/* HTML badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '12px 20px', background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: '12px' }}>
                <span style={{ fontSize: '18px' }}>✅</span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#15803d' }}>Professionelle HTML-E-Mails aktiv</div>
                  <div style={{ fontSize: '12px', color: '#16a34a' }}>Alle E-Mails werden als responsives HTML mit ZBA-Logo, Farben und strukturierten Termindetails versendet – kompatibel mit Outlook, Gmail, Apple Mail und allen anderen Clients.</div>
                </div>
              </div>

              {/* Tab + Viewport row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                {/* Email type tabs */}
                <div style={{ display: 'flex', gap: '4px', background: '#f5f6f8', borderRadius: '12px', padding: '4px', border: '1px solid #e8eaed' }}>
                  {[{ id: 'customer', label: '📧 Kundenbestätigung' }, { id: 'admin', label: '🔔 Admin-Alert' }, { id: 'reminder', label: '⏰ Erinnerung' }]?.map(tab => (
                    <button key={tab?.id} onClick={() => setActiveEmailTab(tab?.id)} style={{ padding: '7px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-ui)', background: activeEmailTab === tab?.id ? 'white' : 'transparent', color: activeEmailTab === tab?.id ? '#1e2b38' : '#8a95a3', boxShadow: activeEmailTab === tab?.id ? '0 1px 4px rgba(30,43,56,0.1)' : 'none', whiteSpace: 'nowrap' }}>{tab?.label}</button>
                  ))}
                </div>

                {/* Viewport toggle */}
                <div style={{ display: 'flex', gap: '4px', background: '#f5f6f8', borderRadius: '10px', padding: '3px', border: '1px solid #e8eaed' }}>
                  {[{ id: 'desktop', icon: '🖥', label: 'Desktop' }, { id: 'mobile', icon: '📱', label: 'Mobile' }, { id: 'outlook', icon: '📨', label: 'Outlook' }]?.map(vp => (
                    <button key={vp?.id} onClick={() => setActiveViewport(vp?.id)} title={vp?.label} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'var(--font-ui)', background: activeViewport === vp?.id ? 'white' : 'transparent', color: activeViewport === vp?.id ? '#1e2b38' : '#8a95a3', boxShadow: activeViewport === vp?.id ? '0 1px 3px rgba(30,43,56,0.12)' : 'none', transition: 'all 150ms' }}>
                      <span style={{ fontSize: '14px' }}>{vp?.icon}</span>
                      <span>{vp?.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Betreff */}
              <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e8eaed', padding: '16px 20px', marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#8a95a3', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>Betreff</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e2b38', fontFamily: 'var(--font-ui)' }}>
                  {activeEmailTab === 'customer' ? settings?.emailTemplates?.customerSubject?.replace('{{booking_ref}}', 'ZBA-20260310-AB3X') : activeEmailTab === 'admin' ? settings?.emailTemplates?.adminSubject?.replace('{{vorname}}', 'Max')?.replace('{{nachname}}', 'Mustermann')?.replace('{{booking_ref}}', 'ZBA-20260310-AB3X') : settings?.emailTemplates?.reminderSubject?.replace('{{booking_ref}}', 'ZBA-20260310-AB3X')}
                </div>
              </div>

              {/* Live HTML Preview */}
              <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e8eaed', overflow: 'hidden', boxShadow: '0 2px 12px rgba(30,43,56,0.08)' }}>
                {/* Browser chrome bar */}
                <div style={{ padding: '10px 16px', background: '#f8f9fb', borderBottom: '1px solid #e8eaed', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ff5f57' }} />
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#febc2e' }} />
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#28c840' }} />
                  </div>
                  <div style={{ flex: 1, background: 'white', borderRadius: '6px', padding: '4px 12px', fontSize: '11px', color: '#8a95a3', border: '1px solid #e8eaed', textAlign: 'center' }}>
                    E-Mail Vorschau – {activeEmailTab === 'customer' ? 'Kundenbestätigung' : activeEmailTab === 'admin' ? 'Admin-Alert' : 'Erinnerung'} · {activeViewport === 'desktop' ? '580px Desktop' : activeViewport === 'mobile' ? '375px Mobile' : '600px Outlook'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#8a95a3', fontWeight: 500, padding: '3px 8px', background: '#e8eaed', borderRadius: '5px' }}>
                    {activeViewport === 'outlook' ? 'Outlook-Ansicht' : activeViewport === 'mobile' ? 'Mobile' : 'Desktop'}
                  </div>
                </div>

                {/* Viewport wrapper */}
                <div style={{ background: activeViewport === 'desktop' ? '#e8eaed' : activeViewport === 'mobile' ? '#1e2b38' : '#f0f0f0', padding: activeViewport === 'mobile' ? '24px 0' : '0', display: 'flex', justifyContent: 'center', minHeight: '640px', transition: 'background 300ms' }}>
                  {activeViewport === 'outlook' ? (
                    // Outlook mode: show a simplified plain-text-like view with a note
                    <div style={{ width: '100%', maxWidth: '600px', background: 'white', padding: '0' }}>
                      <div style={{ background: '#0078d4', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '16px' }}>📨</span>
                        <span style={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>Microsoft Outlook – Vorschau</span>
                      </div>
                      <div style={{ padding: '12px 16px', background: '#f3f2f1', borderBottom: '1px solid #e1dfdd', fontSize: '12px', color: '#323130' }}>
                        <div style={{ marginBottom: '4px' }}><strong>Von:</strong> ZBA Academy &lt;{settings?.notifications?.replyToEmail || 'noreply@zba.academy'}&gt;</div>
                        <div style={{ marginBottom: '4px' }}><strong>An:</strong> Max Mustermann &lt;max@beispiel.de&gt;</div>
                        <div><strong>Betreff:</strong> {activeEmailTab === 'customer' ? settings?.emailTemplates?.customerSubject?.replace('{{booking_ref}}', 'ZBA-20260310-AB3X') : activeEmailTab === 'admin' ? settings?.emailTemplates?.adminSubject?.replace('{{vorname}}', 'Max')?.replace('{{nachname}}', 'Mustermann')?.replace('{{booking_ref}}', 'ZBA-20260310-AB3X') : settings?.emailTemplates?.reminderSubject?.replace('{{booking_ref}}', 'ZBA-20260310-AB3X')}</div>
                      </div>
                      <div style={{ height: '560px', overflow: 'auto' }}>
                        <iframe
                          key={`outlook-${activeEmailTab}`}
                          srcDoc={activeEmailTab === 'customer'
                            ? buildCustomerEmail({ vorname: 'Max', nachname: 'Mustermann', email: 'max@beispiel.de', telefon: '0211 123456', type: 'onsite', date: new Date(Date.now() + 3 * 86400000)?.toISOString(), time: '10:00', berater_name: 'Rainer Gottula', booking_ref: 'ZBA-20260310-AB3X', outlook_link: 'https://outlook.office.com/calendar' })
                            : activeEmailTab === 'admin'
                            ? buildAdminEmail({ vorname: 'Max', nachname: 'Mustermann', email: 'max@beispiel.de', telefon: '0211 123456', wohnort: 'Düsseldorf', type: 'onsite', date: new Date(Date.now() + 3 * 86400000)?.toISOString(), time: '10:00', berater_name: 'Rainer Gottula', berater_email: 'rainer.gottula@zba.academy', booking_ref: 'ZBA-20260310-AB3X', outlook_link: 'https://outlook.office.com/calendar', source: 'webinar', webinar_id: 'WEB-2026-03' })
                            : buildCustomerEmail({ vorname: 'Max', nachname: 'Mustermann', email: 'max@beispiel.de', telefon: '0211 123456', type: 'phone', date: new Date(Date.now() + 1 * 86400000)?.toISOString(), time: '14:00', berater_name: 'Roger Szielenkewitz', booking_ref: 'ZBA-20260310-AB3X', outlook_link: 'https://outlook.office.com/calendar' })
                          }
                          style={{ width: '100%', height: '100%', border: 'none' }}
                          title='Outlook Vorschau'
                          sandbox='allow-same-origin'
                        />
                      </div>
                    </div>
                  ) : (
                    // Desktop / Mobile mode
                    <div style={{
                      width: activeViewport === 'mobile' ? '375px' : '100%',
                      background: 'white',
                      borderRadius: activeViewport === 'mobile' ? '24px' : '0',
                      overflow: 'hidden',
                      boxShadow: activeViewport === 'mobile' ? '0 8px 40px rgba(0,0,0,0.4)' : 'none',
                      border: activeViewport === 'mobile' ? '6px solid #2d2d2d' : 'none',
                      transition: 'all 300ms ease',
                      flexShrink: 0,
                    }}>
                      {activeViewport === 'mobile' && (
                        <div style={{ background: '#2d2d2d', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ width: '60px', height: '6px', borderRadius: '3px', background: '#555' }} />
                        </div>
                      )}
                      <div style={{ height: activeViewport === 'mobile' ? '580px' : '600px', overflow: 'auto' }}>
                        <iframe
                          key={`${activeViewport}-${activeEmailTab}`}
                          srcDoc={activeEmailTab === 'customer'
                            ? buildCustomerEmail({ vorname: 'Max', nachname: 'Mustermann', email: 'max@beispiel.de', telefon: '0211 123456', type: 'onsite', date: new Date(Date.now() + 3 * 86400000)?.toISOString(), time: '10:00', berater_name: 'Rainer Gottula', booking_ref: 'ZBA-20260310-AB3X', outlook_link: 'https://outlook.office.com/calendar' })
                            : activeEmailTab === 'admin'
                            ? buildAdminEmail({ vorname: 'Max', nachname: 'Mustermann', email: 'max@beispiel.de', telefon: '0211 123456', wohnort: 'Düsseldorf', type: 'onsite', date: new Date(Date.now() + 3 * 86400000)?.toISOString(), time: '10:00', berater_name: 'Rainer Gottula', berater_email: 'rainer.gottula@zba.academy', booking_ref: 'ZBA-20260310-AB3X', outlook_link: 'https://outlook.office.com/calendar', source: 'webinar', webinar_id: 'WEB-2026-03' })
                            : buildCustomerEmail({ vorname: 'Max', nachname: 'Mustermann', email: 'max@beispiel.de', telefon: '0211 123456', type: 'phone', date: new Date(Date.now() + 1 * 86400000)?.toISOString(), time: '14:00', berater_name: 'Roger Szielenkewitz', booking_ref: 'ZBA-20260310-AB3X', outlook_link: 'https://outlook.office.com/calendar' })
                          }
                          style={{ width: '100%', height: '100%', border: 'none' }}
                          title='E-Mail Vorschau'
                          sandbox='allow-same-origin'
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Betreff bearbeiten */}
              <div style={{ marginTop: '20px' }}>
                <SectionCard title='Betreff anpassen' subtitle='Betreffzeile der E-Mail'>
                  {activeEmailTab === 'customer' && (
                    <FieldRow label='Betreff' hint='Platzhalter {{booking_ref}} etc. werden ersetzt'><TextInput value={settings?.emailTemplates?.customerSubject} onChange={v => updateSettings('emailTemplates.customerSubject', v)} placeholder='' type='text' /></FieldRow>
                  )}
                  {activeEmailTab === 'admin' && (
                    <FieldRow label='Betreff' hint=''><TextInput value={settings?.emailTemplates?.adminSubject} onChange={v => updateSettings('emailTemplates.adminSubject', v)} placeholder='' type='text' /></FieldRow>
                  )}
                  {activeEmailTab === 'reminder' && (
                    <FieldRow label='Betreff' hint='Platzhalter {{booking_ref}}, {{vorname}} etc. werden ersetzt'><TextInput value={settings?.emailTemplates?.reminderSubject} onChange={v => updateSettings('emailTemplates.reminderSubject', v)} placeholder='' type='text' /></FieldRow>
                  )}
                  <div style={{ padding: '10px 14px', background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '12px', color: '#1e40af', lineHeight: 1.6 }}>
                    💡 Das HTML-Design der E-Mails ist fest in <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: '4px' }}>src/utils/emailTemplates.js</code> definiert und enthält das ZBA-Logo, Termindetails, Kalender-Button und Footer. Für Design-Änderungen diese Datei bearbeiten.
                  </div>
                </SectionCard>
              </div>
            </div>
          )}

          {activeSection === 'notifications' && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e2b38', fontFamily: 'var(--font-heading)', margin: 0 }}>Benachrichtigungseinstellungen</h2>
                <p style={{ fontSize: '13px', color: '#8a95a3', margin: '4px 0 0' }}>Steuern Sie, wer wann welche E-Mails erhaelt.</p>
              </div>
              <SectionCard title='Versandregeln' subtitle='Welche E-Mails werden automatisch versendet?'>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Toggle checked={settings?.notifications?.sendCustomerConfirmation} onChange={v => updateSettings('notifications.sendCustomerConfirmation', v)} label='Buchungsbestaetigung an Kunden senden' />
                  <Toggle checked={settings?.notifications?.sendAdminAlert} onChange={v => updateSettings('notifications.sendAdminAlert', v)} label='Buchungs-Alerts senden' />
                  {settings?.notifications?.sendAdminAlert && (
                    <Toggle checked={settings?.notifications?.onlyNotifyAdmin !== true} onChange={v => updateSettings('notifications.onlyNotifyAdmin', !v)} label='Berater (Rainer & Roger) benachrichtigen' />
                  )}
                  <Toggle checked={settings?.notifications?.sendReminderEmail} onChange={v => updateSettings('notifications.sendReminderEmail', v)} label='Erinnerungs-E-Mail vor dem Termin senden' />
                  <Toggle checked={settings?.notifications?.sendCancellationNotice} onChange={v => updateSettings('notifications.sendCancellationNotice', v)} label='Stornierungsbenachrichtigung senden' />
                </div>
              </SectionCard>
              <SectionCard title='Erinnerungszeitpunkt'>
                <FieldRow label='Stunden vor Termin'><NumberInput value={settings?.notifications?.reminderHoursBefore} onChange={v => updateSettings('notifications.reminderHoursBefore', v)} min={1} max={168} suffix='Stunden' /></FieldRow>
              </SectionCard>
              <SectionCard title='E-Mail-Adressen' subtitle='Empfaenger fuer Admin-Benachrichtigungen'>
                <FieldRow label='Absender-Name'><TextInput value={settings?.notifications?.fromName} onChange={v => updateSettings('notifications.fromName', v)} placeholder='ZBA Academy' /></FieldRow>
                <FieldRow label='Reply-To E-Mail'><TextInput value={settings?.notifications?.replyToEmail} onChange={v => updateSettings('notifications.replyToEmail', v)} placeholder='info@zba.academy' /></FieldRow>
                <FieldRow label='BCC (alle E-Mails)' hint='Optional: alle E-Mails in BCC'><TextInput value={settings?.notifications?.bccAllEmails} onChange={v => updateSettings('notifications.bccAllEmails', v)} placeholder='archiv@zba.academy' /></FieldRow>
                <FieldRow label='Eigene Admin-E-Mail' hint='Deine E-Mail fuer Buchungs-Alerts (immer aktiv)'><TextInput value={settings?.notifications?.ownAdminEmail} onChange={v => updateSettings('notifications.ownAdminEmail', v)} placeholder='w.saris@zba.academy' /></FieldRow>
                <FieldRow label='Berater-Empfaenger' hint={settings?.notifications?.onlyNotifyAdmin ? '⚠️ Berater-Alerts deaktiviert (Toggle oben)' : 'Erhalten Buchungs-Alerts wenn aktiviert'}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', opacity: settings?.notifications?.onlyNotifyAdmin ? 0.4 : 1 }}>
                    {settings?.notifications?.adminEmails?.map((email, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#1e2b38', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: 'white', flexShrink: 0 }}>R</div>
                        <TextInput value={email} onChange={v => { const emails = [...(settings?.notifications?.adminEmails || [])]; emails[idx] = v; updateSettings('notifications.adminEmails', emails); }} />
                      </div>
                    ))}
                  </div>
                </FieldRow>
              </SectionCard>
            </div>
          )}

          {activeSection === 'booking' && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e2b38', fontFamily: 'var(--font-heading)', margin: 0 }}>Buchungseinstellungen</h2>
                <p style={{ fontSize: '13px', color: '#8a95a3', margin: '4px 0 0' }}>Steuern Sie Slot-Dauern, Vorlaufzeiten und Buchungsregeln.</p>
              </div>
              <SectionCard title='Terminarten und Dauern'>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {settings?.booking?.consultationTypes?.map((ct, idx) => (
                    <div key={ct?.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 16px', borderRadius: '10px', background: '#f8f9fb', border: '1px solid #e8eaed' }}>
                      <Toggle checked={ct?.active} onChange={v => { const types = settings?.booking?.consultationTypes?.map((t, i) => i === idx ? { ...t, active: v } : t); updateSettings('booking.consultationTypes', types); }} />
                      <div style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: '#1e2b38' }}>{ct?.label}</div>
                      <NumberInput value={ct?.duration} onChange={v => { const types = settings?.booking?.consultationTypes?.map((t, i) => i === idx ? { ...t, duration: v } : t); updateSettings('booking.consultationTypes', types); }} min={15} max={180} suffix='Min.' />
                    </div>
                  ))}
                </div>
              </SectionCard>
              <SectionCard title='Zeitregeln'>
                <FieldRow label='Vorlaufzeit (min.)' hint='Wie viele Stunden im Voraus muss gebucht werden?'><NumberInput value={settings?.booking?.minAdvanceHours} onChange={v => updateSettings('booking.minAdvanceHours', v)} min={1} max={168} suffix='Stunden' /></FieldRow>
                <FieldRow label='Buchungsfenster' hint='Wie weit in die Zukunft kann gebucht werden?'><NumberInput value={settings?.booking?.advanceBookingDays} onChange={v => updateSettings('booking.advanceBookingDays', v)} min={7} max={365} suffix='Tage' /></FieldRow>
                <FieldRow label='Stornierungsfrist'><NumberInput value={settings?.booking?.cancellationWindowHours} onChange={v => updateSettings('booking.cancellationWindowHours', v)} min={0} max={168} suffix='Stunden' /></FieldRow>
                <FieldRow label='Max. Slots/Tag'><NumberInput value={settings?.booking?.maxSlotsPerDay} onChange={v => updateSettings('booking.maxSlotsPerDay', v)} min={1} max={20} suffix='Slots' /></FieldRow>
              </SectionCard>
              <SectionCard title='Buchungsregeln'>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Toggle checked={settings?.booking?.allowWeekends} onChange={v => updateSettings('booking.allowWeekends', v)} label='Wochenend-Buchungen erlauben' />
                  <Toggle checked={settings?.booking?.requireDSGVO} onChange={v => updateSettings('booking.requireDSGVO', v)} label='DSGVO-Zustimmung erforderlich' />
                  <Toggle checked={settings?.booking?.showWebinarField} onChange={v => updateSettings('booking.showWebinarField', v)} label='Webinar-Feld anzeigen' />
                  <Toggle checked={settings?.booking?.autoConfirm} onChange={v => updateSettings('booking.autoConfirm', v)} label='Buchungen automatisch bestaetigen' />
                </div>
              </SectionCard>
              <SectionCard title='Buchungsreferenz'>
                <FieldRow label='Praefix' hint='z.B. ZBA ergibt ZBA-20260304-AB3X'><TextInput value={settings?.booking?.bookingRefPrefix} onChange={v => updateSettings('booking.bookingRefPrefix', v)} placeholder='ZBA' /></FieldRow>
              </SectionCard>
            </div>
          )}

          {activeSection === 'platform' && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e2b38', fontFamily: 'var(--font-heading)', margin: 0 }}>Plattform und Branding</h2>
                <p style={{ fontSize: '13px', color: '#8a95a3', margin: '4px 0 0' }}>Unternehmensinfos, Farben und Sicherheitseinstellungen.</p>
              </div>
              <SectionCard title='Unternehmensdaten'>
                <FieldRow label='Firmenname'><TextInput value={settings?.platform?.companyName} onChange={v => updateSettings('platform.companyName', v)} /></FieldRow>
                <FieldRow label='Adresse'><TextInput value={settings?.platform?.companyAddress} onChange={v => updateSettings('platform.companyAddress', v)} /></FieldRow>
                <FieldRow label='Telefon'><TextInput value={settings?.platform?.companyPhone} onChange={v => updateSettings('platform.companyPhone', v)} /></FieldRow>
                <FieldRow label='E-Mail'><TextInput value={settings?.platform?.companyEmail} onChange={v => updateSettings('platform.companyEmail', v)} /></FieldRow>
                <FieldRow label='Website'><TextInput value={settings?.platform?.companyWebsite} onChange={v => updateSettings('platform.companyWebsite', v)} /></FieldRow>
              </SectionCard>
              <SectionCard title='Branding'>
                <FieldRow label='Primaerfarbe'>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type='color' value={settings?.platform?.primaryColor || '#3E4954'} onChange={e => updateSettings('platform.primaryColor', e?.target?.value)} style={{ width: '40px', height: '36px', borderRadius: '8px', border: '1.5px solid #dde1e7', cursor: 'pointer', padding: '2px' }} />
                    <TextInput value={settings?.platform?.primaryColor} onChange={v => updateSettings('platform.primaryColor', v)} />
                  </div>
                </FieldRow>
                <FieldRow label='Akzentfarbe'>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type='color' value={settings?.platform?.accentColor || '#1e2b38'} onChange={e => updateSettings('platform.accentColor', e?.target?.value)} style={{ width: '40px', height: '36px', borderRadius: '8px', border: '1.5px solid #dde1e7', cursor: 'pointer', padding: '2px' }} />
                    <TextInput value={settings?.platform?.accentColor} onChange={v => updateSettings('platform.accentColor', v)} />
                  </div>
                </FieldRow>
              </SectionCard>
              <SectionCard title='Sicherheit'>
                <FieldRow label='Dashboard-Passwort' hint='Schuetzt den Zugang zum Admin-Bereich'><TextInput type='password' value={settings?.platform?.dashboardPassword} onChange={v => updateSettings('platform.dashboardPassword', v)} placeholder='Neues Passwort eingeben' /></FieldRow>
              </SectionCard>
              <SectionCard title='Lokalisierung'>
                <FieldRow label='Zeitzone'><SelectInput value={settings?.platform?.timezone} onChange={v => updateSettings('platform.timezone', v)} options={[{ value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' }, { value: 'Europe/Vienna', label: 'Europe/Vienna' }, { value: 'Europe/Zurich', label: 'Europe/Zurich' }, { value: 'UTC', label: 'UTC' }]} /></FieldRow>
                <FieldRow label='Sprache'><SelectInput value={settings?.platform?.language} onChange={v => updateSettings('platform.language', v)} options={[{ value: 'de', label: 'Deutsch' }, { value: 'en', label: 'English' }]} /></FieldRow>
              </SectionCard>
              <SectionCard title='DSGVO-Text' subtitle='Wird im Buchungsformular angezeigt'>
                <TextArea value={settings?.platform?.dsgvoText} onChange={v => updateSettings('platform.dsgvoText', v)} rows={4} placeholder='' />
              </SectionCard>
            </div>
          )}

          {activeSection === 'integrations' && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e2b38', fontFamily: 'var(--font-heading)', margin: 0 }}>Integrationen</h2>
                <p style={{ fontSize: '13px', color: '#8a95a3', margin: '4px 0 0' }}>Status aller externen Dienste und API-Verbindungen.</p>
              </div>
              <SectionCard title='Microsoft Graph / Outlook Calendar' subtitle='Kalender-Sync fuer Berater-Verfuegbarkeit und Termin-Erstellung'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <StatusBadge active={settings?.integrations?.msGraphEnabled} trueLabel='Verbunden' falseLabel='Nicht konfiguriert' />
                  <span style={{ fontSize: '12px', color: '#8a95a3' }}>Credentials werden aus .env geladen</span>
                </div>
                <FieldRow label='Tenant ID'><TextInput value={settings?.integrations?.msTenantId} onChange={v => updateSettings('integrations.msTenantId', v)} placeholder='xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' disabled /></FieldRow>
                <FieldRow label='Client ID'><TextInput value={settings?.integrations?.msClientId} onChange={v => updateSettings('integrations.msClientId', v)} placeholder='xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' disabled /></FieldRow>
                <div style={{ padding: '10px 14px', background: '#fef9c3', borderRadius: '8px', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e' }}>API-Credentials werden server-seitig in der .env-Datei gespeichert (MS_TENANT_ID, MS_CLIENT_ID, MS_CLIENT_SECRET). Status prüfen: /api/health?graph=true</div>
              </SectionCard>
              <SectionCard title='Amazon SES (E-Mail-Versand)' subtitle='SMTP-Konfiguration fuer transaktionale E-Mails'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}><StatusBadge active={settings?.integrations?.sesEnabled} trueLabel='Konfiguriert' falseLabel='Nicht konfiguriert' /></div>
                <FieldRow label='SMTP Host'><TextInput value={settings?.integrations?.sesHost} onChange={v => updateSettings('integrations.sesHost', v)} disabled /></FieldRow>
                <FieldRow label='SMTP Port'><TextInput value={settings?.integrations?.sesPort} onChange={v => updateSettings('integrations.sesPort', v)} disabled /></FieldRow>
                <FieldRow label='Absender-E-Mail'><TextInput value={settings?.integrations?.sesFromEmail} onChange={v => updateSettings('integrations.sesFromEmail', v)} disabled /></FieldRow>
                <div style={{ padding: '10px 14px', background: '#fef9c3', borderRadius: '8px', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e' }}>SMTP-Credentials (VITE_SES_SMTP_USER, VITE_SES_SMTP_PASS) werden sicher in der .env-Datei gespeichert.</div>
              </SectionCard>
              <SectionCard title='Google Analytics' subtitle='Tracking-ID fuer Website-Analyse'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}><StatusBadge active={settings?.integrations?.gaEnabled} trueLabel='Aktiv' falseLabel='Nicht konfiguriert' /></div>
                <FieldRow label='Measurement ID'><TextInput value={settings?.integrations?.gaMeasurementId} onChange={v => updateSettings('integrations.gaMeasurementId', v)} placeholder='G-XXXXXXXXXX' disabled /></FieldRow>
              </SectionCard>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
